/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { isSeverityIconNeeded, MessageStartIcon, MessageSummary } from '../PRIVATE_Message';
import { BundleType } from '../resources/nls/bundle';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { ComponentMessageVariantOptions } from './themes/ComponentMessageStyles.css';
import { ComponentMessageRedwoodTheme } from './themes/redwood/ComponentMessageTheme';

/**
 * A type for a single component message
 */
export type ComponentMessageItem = {
  summary?: string;
  detail?: string;
  severity?: 'confirmation' | 'error' | 'info' | 'warning';
};

/**
 * Props for the Message Component
 */
type Props = {
  /**
   * The error detail text for the component message
   */
  detail?: ComponentMessageItem['detail'];

  /**
   * The label of the field which is showing this error
   */
  fieldLabel?: string;

  /**
   * The severity of the component message
   */
  severity?: ComponentMessageItem['severity'];
};

/**
 * The component that renders an individual message for inline component messaging.
 */
export function ComponentMessage({ detail, fieldLabel, severity = 'error' }: Props) {
  const { classes } = useComponentTheme<ComponentMessageVariantOptions>(
    ComponentMessageRedwoodTheme,
    {
      severity: severity ?? 'none'
    }
  );

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const severityTranslations = {
    confirmation: translations.confirmation(),
    error: translations.error(),
    info: translations.info(),
    warning: translations.warn()
  };

  return (
    <div class={classes} role="alert" aria-atomic="true">
      {fieldLabel && <HiddenAccessible>{fieldLabel}</HiddenAccessible>}
      {isSeverityIconNeeded(severity) && (
        <MessageStartIcon
          variant="inline"
          severity={severity}
          translations={severityTranslations}
        />
      )}
      {/* We will be using the MessageSummary component, as this aligns with the style required */}
      <MessageSummary variant="inline" text={detail} />
    </div>
  );
}
